import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { HubClusters, HubFaq, HubHeader, hubItemList, type HubCluster } from "@/components/hub/ReadingHub";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";
import { faqSchema } from "@/lib/schema";

const FAQ = [
  { q: "What is AEO (Answer Engine Optimization)?", a: "AEO is the practice of structuring content, schema and entity signals so that LLM-powered answer engines — ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews — cite your brand as a source when synthesising answers for users." },
  { q: "How is AEO different from SEO and GEO?", a: "SEO targets the blue-link rankings. GEO (Generative Engine Optimization) covers every generative AI surface including image and shopping models. AEO is the slice of GEO specifically focused on answer engines that return written, cited answers." },
  { q: "Where should I start with AEO?", a: "Begin with the foundations — entity SEO, schema markup, llms.txt — then move into measurement and industry playbooks. The pillar guides below cover every layer in order." },
  { q: "Does AEO replace SEO?", a: "No. Classic SEO still drives the live retrieval index that LLMs use to ground answers. AEO compounds on top of solid SEO foundations; you need both." },
  { q: "How long until AEO produces citations?", a: "No universal citation timeline is established here. Record a baseline and repeated answers under documented conditions; a work schedule is not a guaranteed result deadline." },
];

const CLUSTERS: HubCluster[] = [
  {
    title: "Foundations",
    description: "Start here — the canonical primers on AEO, GEO and how answer engines decide who to cite.",
    slugs: [
      "what-is-aeo-how-to-get-cited-by-chatgpt-2026",
      "aeo-vs-geo-vs-seo-vs-llmo-2026",
      "how-llms-choose-citations-2026",
      "ai-overviews-vs-featured-snippets-2026",
      "chatgpt-vs-perplexity-vs-gemini-vs-ai-overviews",
    ],
  },
  {
    title: "Schema & on-page",
    description: "Make every page machine-readable. Use accurate markup appropriate to visible content; no special AI schema is required by Google.",
    slugs: [
      "schema-markup-for-aeo-2026",
      "llms-txt-explained-2026",
      "ai-first-page-template-html-schema-copy",
      "anatomy-of-chatgpt-cited-paragraph",
      "common-aeo-mistakes-to-avoid",
    ],
  },
  {
    title: "Entity & off-page",
    description: "Get the model to recognise your brand as a known entity, not a string.",
    slugs: [
      "entity-seo-signals-for-aeo",
      "entity-stacking-off-page-aeo",
      "brand-mention-velocity-aeo-signal",
      "zero-to-wikipedia-entity-footprint-b2b",
      "reddit-aeo-playbook-getting-cited-from-threads",
    ],
  },
  {
    title: "Measurement",
    description: "Track citations, share of voice, and refresh cadence — turn AEO from vibes into a dashboard.",
    slugs: [
      "measure-aeo-performance-2026",
      "profound-vs-otterly-vs-athenahq",
      "citation-half-life-chatgpt-forgets-brand",
      "aeo-content-refresh-cadence-re-citation",
      "cite-framework-aeo",
    ],
  },
  {
    title: "Industry playbooks",
    description: "Vertical-specific AEO programs for SaaS, ecommerce, law firms and more.",
    slugs: [
      "aeo-for-saas-complete-playbook-2026",
      "aeo-for-ecommerce-product-schema-playbook",
      "aeo-for-ecommerce-ai-shopping-assistants-guide",
      "aeo-for-law-firms-ymyl-trust-playbook",
      "saas-cited-47-chatgpt-answers-case-study",
    ],
  },
  {
    title: "Source audits & measurement guides",
    description: "Practical source reviews and measurement workflows. The corrected guides distinguish observations from unverified research claims.",
    slugs: [
      "audited-100-pages-cited-by-chatgpt",
      "google-ai-overviews-citation-report-2026",
      "schema-markup-adoption-saas-benchmark-2026",
      "chatgpt-citation-drift-study-2026",
      "unnamed-brand-problem-2000-chatgpt-answers",
      "state-of-aeo-bangladesh-2026-data-report",
    ],
  },
];

export const Route = createFileRoute("/aeo")({
  head: () =>
    buildSeo({
      title: "AEO Hub — Answer Engine Optimization Guides & Measurement",
      description:
        "The complete AEO hub: pillar guides, schema playbooks, entity tactics, measurement tools and industry-specific AEO programs from Freelancer Tamal.",
      path: "/aeo",
      ogEyebrow: "AEO Hub",
      image: "/og/aeo.jpg",
      keywords:
        "AEO, Answer Engine Optimization, AEO guide, AEO playbook, AEO hub, ChatGPT SEO, Perplexity SEO, Google AI Overviews",
    }),
  component: AeoHub,
});

function AeoHub() {
  const itemList = hubItemList("AEO Hub — Curated reading order", CLUSTERS);

  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE.url}/aeo#collection`,
    name: "AEO Hub",
    url: `${SITE.url}/aeo`,
    description:
      "Pillar hub for Answer Engine Optimization — every practical guide, playbook and measurement workflow, organised in reading order.",
    isPartOf: { "@id": `${SITE.url}/#website` },
    about: [
      { "@type": "Thing", name: "Answer Engine Optimization" },
      { "@type": "Thing", name: "Generative Engine Optimization" },
      { "@type": "Thing", name: "Large Language Models" },
    ],
  };

  const faqLd = faqSchema(FAQ);

  return (
    <>
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "AEO Hub", path: "/aeo" },
        ])}
      />
      <JsonLd data={collection} />
      <JsonLd data={itemList} />
      <JsonLd data={faqLd} />

      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "AEO Hub", path: "/aeo" }]} />

      <HubHeader
        eyebrow="AEO Hub"
        title={<>The complete guide to <span className="text-gradient">Answer Engine Optimization.</span></>}
        intro={
          <>
            <strong className="text-foreground">AEO (Answer Engine Optimization)</strong> is how
            modern brands earn citations inside ChatGPT, Perplexity, Claude, Gemini, and Google
            AI Overviews. Source selection and referral behaviour vary by query and assistant.
            This hub orders every pillar guide on freelancertamal.com so you can read the AEO
            program end-to-end, in the order I run it for clients.
          </>
        }
        primary={{ to: "/services/aeo", label: "Work with me on AEO" }}
        secondary={{ to: "/aeo-checklist", label: "Free AEO checklist (PDF)" }}
      />

      <div className="container-x pb-24 space-y-16">
        <HubClusters clusters={CLUSTERS} />

        <HubFaq items={FAQ} />
      </div>
    </>
  );
}